import { IcoClose16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../..';
import useShortcut from '../../hooks/use-shortcut';
import Dialog from '../dialog';
import Footer from './components/footer';
import type { DialogPageButton } from './dialog-page.types';

export type DialogPageProps = {
  documentTitle?: string;
  title: string | React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  primaryButton?: DialogPageButton;
  secondaryButton?: DialogPageButton;
  noPadding?: boolean;
  noScroll?: boolean;
  showHeader?: boolean;
  confirmBeforeClose?: {
    title: string;
    primaryButtonLabel?: string;
    secondaryButtonLabel?: string;
    description: string;
  };
  preventEscapeKeyDown?: boolean;
  bannerComponent?: React.ReactNode;
  hasWarningOutline?: boolean;
};

const DialogPage = forwardRef<HTMLDivElement, DialogPageProps>(
  (
    {
      documentTitle,
      title,
      onClose,
      children,
      primaryButton,
      secondaryButton,
      showHeader = true,
      noPadding = false,
      noScroll = false,
      confirmBeforeClose,
      preventEscapeKeyDown = false,
      bannerComponent,
      hasWarningOutline = false,
    },
    ref,
  ) => {
    const { t } = useTranslation('ui', { keyPrefix: 'components.dialog-page' });
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const isClosingConfirmRef = useRef(false);
    const hasFooter = primaryButton || secondaryButton;

    const openConfirmDialog = () => {
      if (!isClosingConfirmRef.current) {
        setIsConfirmOpen(true);
      }
    };

    const closeConfirmDialog = () => {
      isClosingConfirmRef.current = true;
      setIsConfirmOpen(false);
      setTimeout(() => {
        isClosingConfirmRef.current = false;
      }, 150);
    };

    const handleConfirm = () => {
      closeConfirmDialog();
      onClose();
    };

    const handleClose = () => {
      confirmBeforeClose ? openConfirmDialog() : onClose();
    };

    const handleEscapeKey = preventEscapeKeyDown || !showHeader ? undefined : handleClose;

    useShortcut({
      onEscKeyDown: handleEscapeKey,
      disabled: preventEscapeKeyDown,
    });

    useEffect(() => {
      // Set document title on mount
      if (documentTitle) {
        const originalTitle = document.title;
        document.title = documentTitle;
        return () => {
          document.title = originalTitle;
        };
      }
    }, [documentTitle]);

    return (
      <>
        <div
          aria-label="dialog-page"
          ref={ref}
          className={cx('flex fixed inset-0 flex-col w-screen h-dvh z-dialog bg-primary md:h-screen', {
            'border-[var(--border-color-error)] border-[1.5px] rounded-3xl': hasWarningOutline,
          })}
        >
          <div
            className={cx('flex flex-col flex-grow min-h-full overflow-auto relative', {
              'rounded-xl': hasWarningOutline,
            })}
          >
            {bannerComponent}
            {showHeader && (
              <header
                className={cx(
                  'w-full h-[44px] grid grid-cols-[32px_minmax(0,1fr)_32px] items-center border-b border-tertiary bg-primary px-3 flex-shrink-0 z-10',
                  'md:grid-cols-[minmax(100px,1fr)_auto_minmax(100px,1fr)]',
                  { border: !!bannerComponent },
                )}
              >
                <IconButton
                  icon={IcoClose16}
                  // @ts-ignore: fails for dashboard project due to i18next selector
                  aria-label={t('header-icon.aria-label-default')}
                  onClick={handleClose}
                />
                <div className="text-center text-label-2 max-md:truncate">{title}</div>
              </header>
            )}
            <div
              ref={contentRef}
              className={cx('overflow-auto flex-1 h-full', {
                'p-0': noPadding,
                'p-6': !noPadding,
                'overflow-hidden': noScroll,
              })}
            >
              {children}
            </div>
            {hasFooter && <Footer primaryButton={primaryButton} secondaryButton={secondaryButton} />}
          </div>
        </div>
        {confirmBeforeClose && isConfirmOpen && (
          <Dialog
            open={isConfirmOpen}
            onClose={closeConfirmDialog}
            title={confirmBeforeClose.title}
            isConfirmation={true}
            size="compact"
            onEscapeKeyDown={isConfirmOpen ? closeConfirmDialog : undefined}
            primaryButton={{
              variant: 'destructive',
              // @ts-ignore: fails for dashboard project due to i18next selector
              label: confirmBeforeClose.primaryButtonLabel || t('confirm-before-close.confirm'),
              onClick: handleConfirm,
            }}
            secondaryButton={{
              // @ts-ignore: fails for dashboard project due to i18next selector
              label: confirmBeforeClose.secondaryButtonLabel || t('confirm-before-close.cancel'),
              onClick: closeConfirmDialog,
            }}
          >
            {confirmBeforeClose.description}
          </Dialog>
        )}
      </>
    );
  },
);

export default DialogPage;
